// /src/types/cbs.ts
// CBS Core Data Models - Contract Broiler System
// All interfaces use snake_case to match Supabase database columns.

// ============================================
// BATCH (production cycle)
// ============================================

/**
 * Batch production stage based on bird age
 * - brooding: Days 1-7 (heat lamps, high temp)
 * - early_growth: Days 8-21 (rapid growth phase)
 * - late_growth: Days 22-35 (feed efficiency focus)
 * - finishing: Days 36-42 (market weight)
 */
export type BatchStage = 'brooding' | 'early_growth' | 'late_growth' | 'finishing';

/** Batch lifecycle status — matches batches.batch_status in DB */
export type BatchStatus = 'placed' | 'active' | 'harvesting' | 'completed' | 'abandoned';

/**
 * Batch represents a single production cycle.
 * All fields match the `batches` Supabase table columns exactly.
 */
export interface Batch {
  id: string;
  user_id: string;
  farm_id: string;
  organization_id: string | null;
  breed_id: string;
  country_id: string;
  batch_code: string | null;
  shed_number: string | null;
  doc_count: number;
  placement_date: string;
  doc_source_id: string | null;
  planned_cycle_days: number;
  expected_harvest_date: string;
  actual_harvest_date: string | null;
  batch_status: BatchStatus;
  status_changed_at: string;
  status_changed_by: string | null;
  abandonment_reason: string | null;
  contract_type: string;
  current_bird_count: number | null;
  cumulative_mortality: number | null;
  cumulative_feed_kg: number | null;
  latest_avg_weight_kg: number | null;
  current_fcr: number | null;
  current_production_stage: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Calculate batch stage from current day.
 * Uses hardcoded defaults that mirror the breeds table stage days.
 */
export const getBatchStage = (day: number): BatchStage => {
  if (day <= 7) return 'brooding';
  if (day <= 21) return 'early_growth';
  if (day <= 35) return 'late_growth';
  return 'finishing';
};

// ============================================
// DAILY RECORD (one per day per batch)
// ============================================

/** Cause of bird mortality for tracking and analysis */
export type MortalityCause =
  | 'disease'
  | 'heat_stress'
  | 'cold_stress'
  | 'suffocation'
  | 'predator'
  | 'unknown'
  | 'other';

/** Feed type based on bird age/stage */
export type FeedType = 'starter' | 'grower' | 'finisher';

/**
 * Daily production record for a batch.
 * All fields match the `daily_records` Supabase table columns exactly.
 */
export interface DailyRecord {
  id: string;
  batch_id: string;
  user_id: string;
  country_id: string;
  record_date: string;
  batch_day: number;
  mortality_count: number;
  mortality_cause: string | null;
  culling_count: number | null;
  feed_consumed_kg: number | null;
  feed_type: string | null;
  water_consumed_liters: number | null;
  temperature_min_c: number | null;
  temperature_max_c: number | null;
  humidity_pct: number | null;
  weight_sample_count: number | null;
  weight_sample_avg_g: number | null;
  running_bird_count: number | null;
  running_cumulative_feed_kg: number | null;
  running_fcr: number | null;
  production_stage: string | null;
  is_synced: boolean;
  recorded_at: string;
  recorded_by: string | null;
  created_at: string;
  updated_at: string;
}

// ============================================
// ORGANIC CREDIT SCORE (0-10000)
// Display-only types — not sent to DB directly
// ============================================

/**
 * Organic Credit Score calculation result
 * Composite score from weighted components
 * Range: 0 (min) to 10000 (max)
 */
export interface OrganicCreditScore {
  id: string;
  user_id: string;
  total_score: number;
  fcr_performance_score: number;
  fcr_performance_contribution: number;
  biosecurity_compliance_score: number;
  biosecurity_compliance_contribution: number;
  activity_logging_score: number;
  activity_contribution: number;
  engagement_quality_score: number;
  engagement_contribution: number;
  photo_consistency_score: number;
  photo_contribution: number;
  mortality_performance_score: number;
  mortality_performance_contribution: number;
  settlement_performance_score: number;
  settlement_performance_contribution: number;
  current_streak_days: number;
  percentile_rank: number | null;
  regional_rank: number | null;
  is_eligible_for_loan: boolean;
  scoring_template: string | null;
  calculation_confidence: string | null;
  last_calculated_at: string;
  created_at: string;
  updated_at: string;
}

// ============================================
// FLOCK DNA (5 strands visualization)
// Display-only types
// ============================================

export type FlockDNAStrandId =
  | 'batch_performance'
  | 'biosecurity'
  | 'integrator_rel'
  | 'consistency'
  | 'tech_adoption';

export interface FlockDNAStrand {
  id: FlockDNAStrandId;
  name: string;
  nameBengali: string;
  percentage: number;
  trend: 'up' | 'down' | 'stable';
  color: string;
  contribution: number;
}

export interface FlockDNA {
  farmerId: string;
  batchId?: string;
  strands: FlockDNAStrand[];
  overallHealth: 'excellent' | 'good' | 'fair' | 'needs_improvement' | 'critical';
  updatedAt: string;
}

// ============================================
// CBS ACTIVITY TYPES
// ============================================

export type CBSActivityType =
  | 'placement'
  | 'mortality_report'
  | 'feeding'
  | 'vaccination'
  | 'medication'
  | 'biosecurity_check'
  | 'weighing'
  | 'supervisor_visit'
  | 'catch_out'
  | 'settlement';

// ============================================
// CONSTANTS
// ============================================

export const CBS_CONSTANTS = {
  BATCH_DURATION_DAYS: 42,
  MIN_CREDIT_SCORE: 0,
  MAX_CREDIT_SCORE: 10000,
  BASE_CREDIT_SCORE: 500,
  TARGET_FCR: 1.70,
  TARGET_MORTALITY_PERCENT: 5,

  WEIGHT_BATCH_PERFORMANCE: 0.30,
  WEIGHT_BIOSECURITY: 0.25,
  WEIGHT_INTEGRATOR_REL: 0.20,
  WEIGHT_CONSISTENCY: 0.15,
  WEIGHT_TECH_ADOPTION: 0.10,

  REGIONAL_FCR: {
    dhaka: 1.43,
    chittagong: 1.43,
    rajshahi: 1.49,
    khulna: 1.52,
    sylhet: 1.65,
    mymensingh: 1.75,
  } as Record<string, number>,

  STAGE_DAYS: {
    brooding: [1, 7],
    early_growth: [8, 21],
    late_growth: [22, 35],
    finishing: [36, 42],
  } as Record<BatchStage, [number, number]>,
} as const;
